import { useEffect, useRef, useState } from "react";

/**
 * Hold the screen awake while a rider is running a trip.
 *
 * A rider following guidance puts the phone in a cradle and does not touch it,
 * so the screen sleeps and the journey disappears mid-ride. They then have to
 * unlock and find their place again at exactly the moment their hands are busy.
 *
 * Three things the Wake Lock API gets people wrong, all handled here:
 *
 *  - The lock is released by the browser whenever the page is hidden or the
 *    screen locks anyway. Without re-acquiring on `visibilitychange` it works
 *    once, then silently stops - which is worse than not having it, because
 *    nobody notices until they are on the road.
 *  - It must be released when the trip stops needing it, or a rider's screen is
 *    held on until they close the tab.
 *  - It is unsupported on plenty of browsers, and is rejected outright on a
 *    non-secure origin. Neither is an error worth showing a rider; the feature
 *    simply is not available and the journey works without it.
 *
 * @param {boolean} active hold the screen awake while true
 * @returns {{ supported: boolean, held: boolean }}
 */
export default function useScreenWakeLock(active) {
  const sentinelRef = useRef(null);
  const [held, setHeld] = useState(false);
  const supported = typeof navigator !== "undefined" && "wakeLock" in navigator;

  useEffect(() => {
    if (!supported || !active) return undefined;
    let cancelled = false;

    const release = async () => {
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      setHeld(false);
      if (sentinel && !sentinel.released) {
        try { await sentinel.release(); } catch { /* Already gone; nothing to undo. */ }
      }
    };

    const acquire = async () => {
      if (cancelled || sentinelRef.current || document.visibilityState !== "visible") return;
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) { try { await sentinel.release(); } catch { /* ignore */ } return; }
        sentinelRef.current = sentinel;
        setHeld(true);
        // The browser drops the lock on its own terms; reflect that rather than
        // claiming the screen is still being held.
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
          setHeld(false);
        });
      } catch {
        // Denied, unsupported in this context, or the document was not visible.
        // A rider does not need to know; the trip is unaffected.
        setHeld(false);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") acquire();
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      release();
    };
  }, [active, supported]);

  return { supported, held };
}
